'use client';

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { useTranslations } from 'next-intl'
import { useAuth } from '@/lib/hooks/useAuth';

interface LoginFormProps {
  locale: string;
}

export default function LoginForm({ locale }: LoginFormProps) {
  const t = useTranslations('login');
  
  const { login, error, errors, clearError } = useAuth();
  
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Sayfa yüklendiğinde error state'ini temizle
  useEffect(() => {
    clearError();
  }, [clearError]);

  const validatePhone = (phoneNumber: string) => {
    // 5xx xxx xx xx formatında olmalı
    const phoneRegex = /^5\d{2}\s\d{3}\s\d{2}\s\d{2}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Sadece rakam karakterlerine izin ver
    value = value.replace(/[^\d]/g, '');
    
    // Türkiye ülke kodu varsa kaldır (90)
    if (value.startsWith('90') && value.length > 10) {
      value = value.substring(2);
    }
    
    // Başındaki 0'ı kaldır
    if (value.startsWith('0')) {
      value = value.substring(1);
    }
    
    // Maksimum uzunluk kontrolü (5xx xxx xx xx = 10 rakam)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    // Format: 5xx xxx xx xx
    let formattedValue = '';
    if (value.length > 0) {
      formattedValue = value.substring(0, 3);
    }
    if (value.length > 3) {
      formattedValue += ' ' + value.substring(3, 6);
    }
    if (value.length > 6) {
      formattedValue += ' ' + value.substring(6, 8);
    }
    if (value.length > 8) {
      formattedValue += ' ' + value.substring(8, 10);
    }
    
    setPhone(formattedValue);
    
    if (formattedValue && !validatePhone(formattedValue)) {
      setPhoneError(t('phoneError'));
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Eğer zaten submit işlemi devam ediyorsa, çık
    if (isSubmitting) {
      return;
    }
    
    if (!validatePhone(phone)) {
      setPhoneError(t('phoneError'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Remove spaces from phone number for API
      const phoneNumber = phone.replace(/\s/g, '');
      
      const success = await login({
        phoneNumber,
        password,
        rememberMe
      });
      
      if (success) {
        // HttpOnly cookie'ler server-side'da set edildi
        // window.location.href kullanarak tam refresh yap
        // Bu sayede middleware tekrar çalışır ve server component'ler cookie'leri okur
        // User bilgisi state'den gelecek ama henüz yüklenmemiş olabilir
        // O yüzden direkt home'a yönlendir, middleware admin kontrolü yapacak
        window.location.href = `/${locale}`;
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      // Error handling
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sol taraf - Arka plan resmi ve içerik */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/images/login-bg.jpg)',
            backgroundSize: 'cover'
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          {/* Logo */}
          <div>
            <h1 className="text-4xl font-bold text-white">{t('heroTitle')}</h1>
          </div>
          
          {/* Ana içerik */}
          <div className="max-w-md">
            <h2 className="text-5xl font-bold text-white mb-4 leading-tight">
              {t('heroSubtitle')}
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('heroDescription')}
            </p>
          </div>
          
          {/* Footer */}
          <div>
            <p className="text-sm text-white">{t('copyright')}</p>
          </div>
        </div>
      </div>
      
      {/* Sağ taraf - Giriş formu */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo ve Başlık */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4 sm:mb-6">
              <Image
                src="/images/logo.png"
                alt="Trink Emlak - Gayrimenkul Açık Artırma Platformu"
                width={300}
                height={135}
                className="h-20 sm:h-30 w-auto"
                priority
                fetchPriority="high"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('title')}</h1>
          </div>
          
          {/* Giriş formu */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Telefon numarası */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('phone')} {t('required')}
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9846 21.5573 21.2136 21.3521 21.4019C21.1469 21.5902 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0973 21.9452 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3146 6.72533 15.2661 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.09477 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65171C2.82196 2.44673 3.04971 2.28291 3.30351 2.17052C3.55731 2.05813 3.83166 2.00026 4.10999 2H7.10999C7.59522 1.99522 8.06577 2.16708 8.43376 2.48353C8.80175 2.79999 9.042 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97348 7.27675 9.89381 7.64958C9.81415 8.02242 9.62976 8.36256 9.35999 8.63L8.08999 9.9C9.51355 12.4136 11.5864 14.4865 14.1 15.91L15.37 14.64C15.6374 14.3702 15.9776 14.1858 16.3504 14.1062C16.7233 14.0265 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="absolute left-10 top-1/2 transform -translate-y-1/2 z-10 text-gray-500 font-medium text-base">
                  +90
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  autoComplete="tel-national"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="5xx xxx xx xx"
                  maxLength={19}
                  className={`w-full pl-20 pr-4 py-3 sm:py-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm text-base ${
                    phoneError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
              </div>
              {phoneError && (
                <p className="text-red-500 text-sm mt-1">{phoneError}</p>
              )}
            </div>
            
            {/* Şifre */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('password')} {t('required')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('passwordPlaceholder')}
                  className="w-full px-10 py-3 sm:py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm text-base"
                  required
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2"/>
                    <circle cx="12" cy="16" r="1" fill="#9CA3AF"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="#9CA3AF" strokeWidth="2"/>
                  </svg>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                                      ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12C2 12 5.63636 6 12 6C18.3636 6 22 12 22 12C22 12 18.3636 18 12 18C5.63636 18 2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                </button>
              </div>
            </div>
            
            {/* Beni hatırla ve şifremi unuttum */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-900">
                  {t('rememberMe')}
                </label>
              </div>
              <Link 
                href={`/${locale}/sifremi-unuttum`}
                className="text-sm font-medium text-red-600 hover:text-red-500"
              >
                {t('forgotPassword')}
              </Link>
            </div>
            
            {/* Error message */}
            {(error || errors.length > 0) && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                {error && (
                  <p className="text-red-600 text-sm mb-2">{error}</p>
                )}
                {errors.length > 0 && (
                  <div>
                    {errors.map((err: string, index: number) => (
                      <p key={index} className="text-red-600 text-sm">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Giriş butonu */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-600 text-white py-3 sm:py-4 px-4 rounded-md font-medium text-base sm:text-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('loggingIn') || 'Giriş yapılıyor...' : t('loginButton')}
            </button>
          </form>
          
          {/* Kayıt ol linki */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-600">
              {t('noAccount')}{" "}
              <Link 
                href={`/${locale}/kayit`}
                className="font-medium text-red-600 hover:text-red-500"
              >
                {t('createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
